import { useId, useRef, useState, type ReactNode } from 'react';
import { T, useGT, Var } from 'gt-react';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import {
  DEFAULT_TASK_OUTCOMES,
  type TaskExpectedSubtask,
} from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { CoreEnumPicker } from 'src/components/core/CoreEnumPicker';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import { useCreateTaskType } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import { SchemaDefinitionPicker } from 'src/components/schema-definitions/SchemaDefinitionPicker';
import { NamespaceField } from 'src/components/namespaces/NamespaceField';
import { toast } from 'src/components/ui/toast';
import { cn } from 'src/lib/utils';
import {
  ACTION_FORM_FIELD_TYPE_OPTIONS,
  isValidTaskTypeKey,
  newActionDraft,
  newActionFormFieldDraft,
  NO_ANCHOR_VALUE,
  NO_INTENDED_ASSIGNEE_VALUE,
  NO_OUTCOME_ENUM_VALUE,
  NO_NAMESPACE_VALUE,
  NO_INPUT_SCHEMA_VALUE,
  parseActionDrafts,
  sortedAnchorEntityOptions,
  taskTypeKeyFromName,
  type ActionDraftError,
  type ActionFormFieldDraft,
  type TaskTypeActionDraft,
  type TaskTypeEditorState,
} from './taskTypeAdmin';
import {
  mergeAppliedStages,
  parseIntendedAssignee,
  type TaskTypeStageOption,
} from './taskTypeStageTree';
import { TaskTypePersistedStageTree } from './TaskTypePersistedStageTree';
import { TaskTypeActionCloseGuardrails } from './TaskTypeActionCloseGuardrails';
import { TaskTypeGenerateStagesPanel } from './TaskTypeGenerateStagesPanel';

function formatActionDraftError(
  gt: ReturnType<typeof useGT>,
  error: ActionDraftError,
): string {
  const n = String(error.index + 1);
  const identity = formatActionIdentityError(gt, error, n);
  if (identity) return identity;
  const effect = formatActionEffectError(gt, error, n);
  if (effect) return effect;
  return formatActionOutcomeError(gt, error, n);
}

function formatActionIdentityError(
  gt: ReturnType<typeof useGT>,
  error: ActionDraftError,
  n: string,
): string | undefined {
  if (error.code === 'key_required') {
    return gt('Action {n}: key is required', {
      context: 'task type action validation error',
      n,
    });
  }
  if (error.code === 'key_invalid') {
    return gt('Action {n}: key must be lowercase kebab-case', {
      context: 'task type action validation error',
      n,
    });
  }
  if (error.code === 'key_duplicate') {
    return gt('Action {n}: duplicate key "{key}"', {
      context: 'task type action validation error',
      n,
      key: error.key ?? '',
    });
  }
  return undefined;
}

function formatActionEffectError(
  gt: ReturnType<typeof useGT>,
  error: ActionDraftError,
  n: string,
): string | undefined {
  if (error.code === 'label_required') {
    return gt('Action {n}: label is required', {
      context: 'task type action validation error',
      n,
    });
  }
  if (error.code === 'event_key_required') {
    return gt('Action {n}: event key is required', {
      context: 'task type action validation error',
      n,
    });
  }
  if (error.code === 'workflow_template_key_required') {
    return gt('Action {n}: workflow template key is required', {
      context: 'task type action validation error',
      n,
    });
  }
  return undefined;
}

function formatActionOutcomeError(
  gt: ReturnType<typeof useGT>,
  error: ActionDraftError,
  n: string,
): string {
  if (error.code === 'outcome_required') {
    return gt('Action {n}: an outcome is required', {
      context: 'task type action validation error',
      n,
    });
  }
  if (error.code === 'outcome_reason_required') {
    return gt(
      'Action {n}: a cancellation reason is required, or add a form field named outcomeReason',
      {
        context: 'task type action validation error',
        n,
      },
    );
  }
  return gt('Action {n}: {detail}', {
    context: 'task type action schema validation error',
    n,
    detail:
      error.detail ??
      gt('Invalid actions', {
        context: 'fallback task type action schema validation error',
      }),
  });
}

export function FieldBlock({
  label,
  htmlFor,
  description,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      <p className="text-xs leading-relaxed text-muted-foreground">
        {description}
      </p>
      {children}
    </div>
  );
}

export function TaskTypeSection({
  title,
  description,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="gap-0 overflow-hidden bg-card p-0 shadow-none">
      <CardHeader className="border-b bg-muted/60 px-4 py-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? (
          <CardDescription className="text-xs leading-relaxed">
            {description}
          </CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 bg-card p-4">
        {children}
      </CardContent>
    </Card>
  );
}

function TaskTypeKeyHint({
  keyDisabled,
  nameFirst,
}: {
  keyDisabled: boolean;
  nameFirst: boolean;
}) {
  if (keyDisabled) {
    return (
      <T context="hint under a locked task type key field">
        Stable machine ID used by workflows. Immutable after creation.
      </T>
    );
  }
  if (nameFirst) {
    return (
      <T context="hint under auto-filled task type key from name">
        Suggested from the name (kebab-case). You can edit it; it becomes
        immutable after creation.
      </T>
    );
  }
  return (
    <T context="hint under the task type key field">
      Stable machine ID used by workflows (kebab-case). Immutable after creation
      — choose carefully.
    </T>
  );
}

export function TaskTypeIdentityFields({
  keyValue,
  name,
  description,
  keyDisabled,
  disabled,
  showDescription = true,
  nameFirst = false,
  sideBySide = false,
  onKeyChange,
  onNameChange,
  onDescriptionChange,
}: {
  keyValue: string;
  name: string;
  description: string;
  keyDisabled: boolean;
  disabled?: boolean;
  showDescription?: boolean;
  nameFirst?: boolean;
  sideBySide?: boolean;
  onKeyChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
}) {
  const gt = useGT();
  const idPrefix = useId();
  const keyId = `${idPrefix}-key`;
  const nameId = `${idPrefix}-name`;
  const descriptionId = `${idPrefix}-description`;

  const nameField = (
    <FieldBlock
      htmlFor={nameId}
      label={<T context="task type form field label">Name</T>}
      description={
        <T context="hint under the task type name field">
          Human-readable label shown in the queue, inbox, and settings.
        </T>
      }
    >
      <Input
        id={nameId}
        value={name}
        disabled={disabled}
        onChange={(e) => onNameChange(e.target.value)}
        autoFocus={nameFirst && !keyDisabled}
      />
    </FieldBlock>
  );

  const keyField = (
    <FieldBlock
      htmlFor={keyId}
      label={<T context="task type form field label">Key</T>}
      description={
        <TaskTypeKeyHint keyDisabled={keyDisabled} nameFirst={nameFirst} />
      }
    >
      <Input
        id={keyId}
        value={keyValue}
        disabled={keyDisabled || disabled}
        onChange={(e) => onKeyChange(e.target.value)}
        placeholder={gt('approve-invoice', {
          context: 'example task type key placeholder',
        })}
      />
      {!keyDisabled && keyValue.length > 0 && !isValidTaskTypeKey(keyValue) && (
        <p className="text-[11px] text-destructive">
          <T context="task type key validation error">
            Use lowercase kebab-case (e.g. &quot;approve-invoice&quot;).
          </T>
        </p>
      )}
    </FieldBlock>
  );

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-4',
          sideBySide && 'sm:grid sm:grid-cols-2 sm:items-start',
        )}
      >
        {nameFirst ? (
          <>
            {nameField}
            {keyField}
          </>
        ) : (
          <>
            {keyField}
            {nameField}
          </>
        )}
      </div>

      {showDescription && (
        <FieldBlock
          htmlFor={descriptionId}
          label={<T context="task type form field label">Description</T>}
          description={
            <T context="hint under the task type description field">
              Optional context for operators configuring workflows against this
              type.
            </T>
          }
        >
          <Textarea
            id={descriptionId}
            rows={2}
            value={description}
            disabled={disabled}
            onChange={(e) => onDescriptionChange(e.target.value)}
          />
        </FieldBlock>
      )}
    </>
  );
}

export function TaskTypeBehaviorFields({
  editor,
  disabled,
  onChange,
}: {
  editor: TaskTypeEditorState;
  disabled?: boolean;
  onChange: (next: TaskTypeEditorState) => void;
}) {
  const gt = useGT();
  const idPrefix = useId();
  const defaultOutcomesLabel = DEFAULT_TASK_OUTCOMES.join(', ');

  const anchorOptions: SelectDropdownOption[] = [
    {
      id: NO_ANCHOR_VALUE,
      label: gt('None — not tied to a business record', {
        context: 'anchor entity type option, no anchoring',
      }),
      display: gt('None', {
        context: 'anchor entity type short display, no anchoring',
      }),
    },
    ...sortedAnchorEntityOptions().map((option) => ({
      id: option.id,
      label: option.label,
      display: option.label,
    })),
  ];

  const selectedAnchor =
    anchorOptions.find((option) => option.id === editor.anchorEntityType) ??
    anchorOptions[0];

  return (
    <>
      <FieldBlock
        label={<T context="task type form field label">Anchor entity type</T>}
        description={
          <T context="hint under the anchor entity type picker">
            When set, open tasks of this type are unique per entity (e.g. one
            open &quot;approve invoice&quot; task per AP invoice). Leave None
            for free-floating work.
          </T>
        }
      >
        <SelectDropdown
          options={anchorOptions}
          value={selectedAnchor}
          onChange={(option) =>
            onChange({
              ...editor,
              anchorEntityType: option?.id ?? NO_ANCHOR_VALUE,
            })
          }
          placeholder={gt('Search entity types…', {
            context: 'placeholder for searchable anchor entity picker',
          })}
          searchPlaceholder={gt('Search…', {
            context: 'search box in the anchor entity picker',
          })}
          size="sm"
          fullWidth
          disabled={disabled}
        />
      </FieldBlock>

      <FieldBlock
        label={<T context="task type form field label">Completion mode</T>}
        description={
          <T context="hint under the completion mode picker">
            Controls when a parent task of this type is allowed to close
            relative to its expected subtask checklist.
          </T>
        }
      >
        <RadioGroup
          value={editor.completionMode}
          disabled={disabled}
          onValueChange={(value) => {
            if (value === 'explicit' || value === 'auto') {
              onChange({ ...editor, completionMode: value });
            }
          }}
          className="flex flex-col gap-2 rounded-md border border-border p-2.5"
        >
          <label
            htmlFor={`${idPrefix}-completion-explicit`}
            className="flex cursor-pointer gap-2.5 rounded-md p-1.5 hover:bg-muted/50"
          >
            <RadioGroupItem
              value="explicit"
              id={`${idPrefix}-completion-explicit`}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="block text-sm font-medium">
                <T context="completion mode option title">
                  Explicit — wait for checklist
                </T>
              </span>
              <span className="block text-xs leading-relaxed text-muted-foreground">
                <T context="explanation of explicit completion mode">
                  Parent stays open until every expected subtask type exists and
                  is closed, and something calls complete. Use for staged work
                  like extract → review → approve.
                </T>
              </span>
            </span>
          </label>
          <label
            htmlFor={`${idPrefix}-completion-auto`}
            className="flex cursor-pointer gap-2.5 rounded-md p-1.5 hover:bg-muted/50"
          >
            <RadioGroupItem
              value="auto"
              id={`${idPrefix}-completion-auto`}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="block text-sm font-medium">
                <T context="completion mode option title">
                  Auto — not gated on checklist
                </T>
              </span>
              <span className="block text-xs leading-relaxed text-muted-foreground">
                <T context="explanation of auto completion mode">
                  Closing is not blocked by the checklist. Use when subtasks are
                  informational and the parent can finish on its own.
                </T>
              </span>
            </span>
          </label>
        </RadioGroup>
      </FieldBlock>

      <FieldBlock
        label={
          <T context="task type form field label">How can this task end?</T>
        }
        description={
          <T context="hint under the outcome vocabulary picker">
            When a task of this type is closed, the closer picks one finishing
            result (the outcome). That value is stored on the task for reporting
            and workflows. Leave Default for success / abandoned, or choose a
            Core Enum whose option names are the allowed results (for example
            approved, rejected, needs-resubmit).
          </T>
        }
      >
        <CoreEnumPicker
          value={
            editor.outcomeEnumId === NO_OUTCOME_ENUM_VALUE
              ? null
              : editor.outcomeEnumId
          }
          onChange={(enumId) =>
            onChange({
              ...editor,
              outcomeEnumId: enumId ?? NO_OUTCOME_ENUM_VALUE,
            })
          }
          allowNone
          disabled={disabled}
          noneLabel={gt('Default ({outcomes}) — platform built-in endings', {
            context: 'outcome vocabulary none option using platform defaults',
            outcomes: defaultOutcomesLabel,
          })}
          noneDisplay={gt('Default', {
            context: 'outcome vocabulary short display for platform defaults',
          })}
          helpText={
            editor.outcomeEnumId === NO_OUTCOME_ENUM_VALUE ? (
              <T context="extra explanation for default task outcomes">
                Default endings: <Var>{defaultOutcomesLabel}</Var>. Create a
                custom list if operators need domain-specific results when they
                complete or cancel the task.
              </T>
            ) : (
              <T context="extra explanation for custom task outcome enum">
                Option names on the selected enum become the only outcomes that
                can be written when this task type closes.
              </T>
            )
          }
          createDescription={
            <T context="create enum dialog description for task outcomes">
              Create the list of allowed finishing results for this task type.
              Each option name is stored as the outcome when someone closes a
              task (prefer short lowercase names like approved or rejected).
            </T>
          }
          optionNamePlaceholder={gt('e.g. approved', {
            context:
              'placeholder for an outcome option name in the enum editor',
          })}
        />
      </FieldBlock>
    </>
  );
}

/**
 * Default namespace picker: assigns the namespace that tasks of this type
 * are placed (and numbered) in when no explicit namespace is given. Includes
 * an inline "create namespace" affordance.
 */
export function TaskTypeNamespaceField({
  value,
  disabled,
  onChange,
}: {
  /** NO_NAMESPACE_VALUE or a namespace id. */
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const gt = useGT();

  return (
    <FieldBlock
      label={<T context="task type form field label">Default namespace</T>}
      description={
        <T context="hint under the default namespace picker">
          Tasks created with this type and no explicit namespace are placed and
          numbered in this namespace. Changing it only affects future tasks —
          existing tasks keep their namespace and number.
        </T>
      }
    >
      <NamespaceField
        value={value}
        onChange={onChange}
        disabled={disabled}
        noneOption={{
          id: NO_NAMESPACE_VALUE,
          label: gt('Organization default — no assigned namespace', {
            context: 'default namespace option, fall back to the org default',
          }),
          display: gt('Organization default', {
            context: 'default namespace short display, org default fallback',
          }),
        }}
        compact
      />
    </FieldBlock>
  );
}

/**
 * Optional schema definition that callers of create-task must satisfy.
 * Changing it only affects future tasks — existing payloads stay as stored.
 */
export function TaskTypeInputSchemaField({
  value,
  disabled,
  onChange,
}: {
  /** NO_INPUT_SCHEMA_VALUE or a schema definition id. */
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const gt = useGT();

  return (
    <FieldBlock
      label={
        <T context="task type form field label for the create-time input schema">
          Input schema definition
        </T>
      }
      description={
        <T context="hint under the task type input schema picker">
          The create-time input contract. Pick an existing schema definition, or
          create one here. Callers must pass every required field on that
          definition, and the validated payload is stored on the task. Leave
          None if this type does not need typed input. Changing it only affects
          future tasks.
        </T>
      }
    >
      <SchemaDefinitionPicker
        value={value}
        disabled={disabled}
        onChange={onChange}
        noneOption={{
          id: NO_INPUT_SCHEMA_VALUE,
          label: gt('None — callers pass no typed input', {
            context: 'task type input schema option, no contract',
          }),
          display: gt('None', {
            context: 'task type input schema short display, no contract',
          }),
        }}
        createTitle={
          <T context="dialog title when creating a schema definition for task input">
            New task input schema
          </T>
        }
        createDefaults={{
          schemaType: 'extraction',
          targetModel: 'TASK_TYPE_INPUT',
        }}
      />
    </FieldBlock>
  );
}

export type { TaskTypeStageOption } from './taskTypeStageTree';

export function TaskTypeStagesEditor({
  editor,
  stageOptions,
  disabled,
  onChange,
}: {
  editor: TaskTypeEditorState;
  stageOptions: TaskTypeStageOption[];
  disabled?: boolean;
  onChange: (next: TaskTypeEditorState) => void;
}) {
  const gt = useGT();
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const selectedKeys = new Set(
    editor.expectedSubtasks.map((entry) => entry.key),
  );
  const addableOptions: SelectDropdownOption[] = stageOptions
    .filter(
      (option) => option.key !== editor.key && !selectedKeys.has(option.key),
    )
    .map((option) => ({
      id: option.key,
      label: `${option.name} (${option.key})`,
      display: option.name,
    }));

  function applyEditorUpdate(
    update: (current: TaskTypeEditorState) => TaskTypeEditorState,
  ) {
    onChange(update(editorRef.current));
  }

  function setStages(expectedSubtasks: TaskExpectedSubtask[]) {
    applyEditorUpdate((current) => ({ ...current, expectedSubtasks }));
  }

  function moveStage(index: number, direction: -1 | 1) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= editor.expectedSubtasks.length) return;
    const next = [...editor.expectedSubtasks];
    const current = next[index];
    const swap = next[nextIndex];
    if (!current || !swap) return;
    next[index] = swap;
    next[nextIndex] = current;
    setStages(next);
  }

  const hasNestedStages = editor.expectedSubtasks.some((entry) => {
    const option = stageOptions.find(
      (candidate) => candidate.key === entry.key,
    );
    return (option?.expectedSubtasks.length ?? 0) > 0;
  });

  return (
    <TaskTypeSection
      title={<T context="task type form field label">Expected stages</T>}
      description={
        <T context="hint under the expected stages editor">
          Ordered checklist of child task types. Each stage is another type in
          this registry — pick an existing one or create a new type and add it
          here. Nested subtasks of a stage are shown underneath. The Designed
          for setting tells automation scoring whether the stage is meant for an
          agent or a human; it does not assign a specific person.
        </T>
      }
    >
      <div className="flex flex-col gap-3">
        {/* Applying an AI-generated tree persists via applyStageTree, which
            needs the parent row to exist — hide the panel until then. */}
        {!disabled && editor.editing && (
          <TaskTypeGenerateStagesPanel
            editor={editor}
            stageOptions={stageOptions}
            disabled={disabled}
            defaultOpen={editor.expectedSubtasks.length === 0}
            onApplied={(saved) =>
              applyEditorUpdate((current) => mergeAppliedStages(current, saved))
            }
          />
        )}
        {!disabled && !editor.editing && (
          <p className="text-[11px] text-muted-foreground">
            <T context="hint that AI stage generation requires the task type to exist first">
              Generating stages with AI becomes available after the type is
              created.
            </T>
          </p>
        )}
        {editor.expectedSubtasks.length === 0 ? (
          <p className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-4 text-xs text-muted-foreground">
            {editor.editing ? (
              <T context="empty state for task type expected stages, editing an existing type">
                No stages yet. Generate with AI, add an existing type, or create
                a new child type.
              </T>
            ) : (
              <T context="empty state for task type expected stages, creating a new type">
                No stages yet. Add an existing type or create a new child type.
              </T>
            )}
          </p>
        ) : (
          <>
            <TaskTypePersistedStageTree
              entries={editor.expectedSubtasks}
              stageOptions={stageOptions}
              disabled={disabled}
              onMove={moveStage}
              onRemove={(key) =>
                setStages(
                  editor.expectedSubtasks.filter(
                    (candidate) => candidate.key !== key,
                  ),
                )
              }
              onIntendedAssigneeChange={(key, intendedAssignee) =>
                setStages(
                  editor.expectedSubtasks.map((candidate) =>
                    candidate.key === key
                      ? { ...candidate, intendedAssignee }
                      : candidate,
                  ),
                )
              }
            />
            {hasNestedStages ? (
              <p className="text-[11px] text-muted-foreground">
                <T context="hint that nested subtasks of a stage are shown under it">
                  Nested subtasks belong to the child type. Open that type to
                  reorder or remove them.
                </T>
              </p>
            ) : null}
          </>
        )}

        {!disabled && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-56 flex-1">
              <SelectDropdown
                options={addableOptions}
                value={null}
                onChange={(option) => {
                  if (!option) return;
                  setStages([...editor.expectedSubtasks, { key: option.id }]);
                }}
                placeholder={gt('Add an existing type…', {
                  context:
                    'placeholder to add an existing task type as a stage',
                })}
                searchPlaceholder={gt('Search…', {
                  context: 'search box when adding an existing stage type',
                })}
                size="sm"
                fullWidth
                disabled={addableOptions.length === 0}
              />
            </div>
            <CreateStageTypeButton
              existingKeys={
                new Set([
                  editor.key,
                  ...stageOptions.map((option) => option.key),
                ])
              }
              onCreated={(entry) =>
                applyEditorUpdate((current) => ({
                  ...current,
                  expectedSubtasks: [...current.expectedSubtasks, entry],
                }))
              }
            />
          </div>
        )}
      </div>
    </TaskTypeSection>
  );
}

function CreateStageTypeButton({
  existingKeys,
  onCreated,
}: {
  existingKeys: Set<string>;
  onCreated: (entry: TaskExpectedSubtask) => void;
}) {
  const gt = useGT();
  const createTaskType = useCreateTaskType();
  const [open, setOpen] = useState(false);
  const keyTouched = useRef(false);
  const [draft, setDraft] = useState({
    key: '',
    name: '',
    intendedAssignee: NO_INTENDED_ASSIGNEE_VALUE,
  });

  function resetAndClose() {
    keyTouched.current = false;
    setDraft({
      key: '',
      name: '',
      intendedAssignee: NO_INTENDED_ASSIGNEE_VALUE,
    });
    setOpen(false);
  }

  async function handleCreate() {
    const key = draft.key.trim();
    const name = draft.name.trim();
    if (!isValidTaskTypeKey(key) || !name) return;
    if (existingKeys.has(key)) {
      toast.error(
        gt('A task type with this key already exists', {
          context: 'error creating a child stage type with a duplicate key',
        }),
      );
      return;
    }
    try {
      await createTaskType.mutateAsync({ key, name });
      onCreated({
        key,
        intendedAssignee: parseIntendedAssignee(draft.intendedAssignee),
      });
      resetAndClose();
      toast.success(
        gt('Stage type created', {
          context:
            'toast after creating a child task type from the stages editor',
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to create the stage type', {
              context: 'toast after creating a child stage type failed',
            }),
      );
    }
  }

  const canCreate =
    isValidTaskTypeKey(draft.key) &&
    draft.name.trim().length > 0 &&
    !createTaskType.isPending;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
      >
        <PlusIcon className="size-3.5" />
        <T context="button, create a new child task type as a stage">
          Create new type
        </T>
      </Button>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) resetAndClose();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <T context="dialog title, create a child task type to use as a stage">
                New stage type
              </T>
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <TaskTypeIdentityFields
              keyValue={draft.key}
              name={draft.name}
              description=""
              keyDisabled={false}
              showDescription={false}
              nameFirst
              onKeyChange={(key) => {
                keyTouched.current = true;
                setDraft({ ...draft, key });
              }}
              onNameChange={(name) =>
                setDraft({
                  ...draft,
                  name,
                  key: keyTouched.current
                    ? draft.key
                    : taskTypeKeyFromName(name),
                })
              }
              onDescriptionChange={() => undefined}
            />
            <FieldBlock
              label={<T context="task type form field label">Designed for</T>}
              description={
                <T context="hint under designed-for picker when creating a stage type">
                  Who this stage is meant for. Unset infers from how the subtask
                  is actually assigned at runtime.
                </T>
              }
            >
              <Select
                value={draft.intendedAssignee}
                onValueChange={(intendedAssignee) =>
                  setDraft({ ...draft, intendedAssignee })
                }
              >
                <SelectTrigger
                  className="h-8"
                  aria-label={gt('Designed for this stage type', {
                    context:
                      'accessible label for designed-for picker when creating a stage type',
                  })}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_INTENDED_ASSIGNEE_VALUE}>
                    {gt('Unset (infer)', {
                      context:
                        'create stage type designed-for option, infer from actual assignment',
                    })}
                  </SelectItem>
                  <SelectItem value="agent">
                    {gt('Agent / automation', {
                      context: 'create stage type designed-for option',
                    })}
                  </SelectItem>
                  <SelectItem value="human">
                    {gt('Human', {
                      context: 'create stage type designed-for option',
                    })}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FieldBlock>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={resetAndClose}>
              <T context="button, cancel creating a stage type">Cancel</T>
            </Button>
            <Button disabled={!canCreate} onClick={() => void handleCreate()}>
              {createTaskType.isPending ? (
                <T context="button label while creating a stage type">
                  Creating…
                </T>
              ) : (
                <T context="button, create the stage type">Create</T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function useTaskTypeOutcomeChoices(outcomeEnumId: string): string[] {
  const custom = outcomeEnumId !== NO_OUTCOME_ENUM_VALUE;
  const { data } = useCoreEnums(
    { first: 100, includeOptions: true },
    { enabled: custom, staleTime: 30_000 },
  );
  if (!custom) return [...DEFAULT_TASK_OUTCOMES];
  const match = data?.edges.find((entry) => entry.id === outcomeEnumId);
  return (match?.options ?? [])
    .map((option) => option.name)
    .filter(
      (name): name is string => typeof name === 'string' && name.length > 0,
    );
}

export function TaskTypeActionsEditor({
  drafts,
  outcomeEnumId,
  disabled,
  onChange,
}: {
  drafts: TaskTypeActionDraft[];
  /** NO_OUTCOME_ENUM_VALUE or the type's outcome Core Enum id. */
  outcomeEnumId: string;
  disabled?: boolean;
  onChange: (next: TaskTypeActionDraft[]) => void;
}) {
  const gt = useGT();
  const outcomeChoices = useTaskTypeOutcomeChoices(outcomeEnumId);
  const parsed = parseActionDrafts(drafts);

  function updateDraft(clientId: string, patch: Partial<TaskTypeActionDraft>) {
    onChange(
      drafts.map((draft) =>
        draft.clientId === clientId ? { ...draft, ...patch } : draft,
      ),
    );
  }

  return (
    <TaskTypeSection
      title={<T context="task type form field label">Custom actions</T>}
      description={
        <T context="hint under the task type actions editor">
          Optional buttons on the task detail page and as quick actions on the
          tasks queue when they have no form. Each action emits a system event,
          starts a workflow, or records an outcome that closes the task.
          Recording an outcome is the same result a complete-task workflow node
          would write, so workflows waiting on the task can continue without
          that extra node. Adding any action replaces the default Complete, Fail
          and Cancel buttons, so include an action for each way the task should
          close. Leave empty to keep the default buttons.
        </T>
      }
    >
      <div className="flex flex-col gap-3">
        {drafts.map((draft, index) => (
          <div
            key={draft.clientId}
            className="flex flex-col gap-2 rounded-md border border-border bg-muted/50 p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-muted-foreground">
                <T context="heading for one custom task action row">
                  Action <Var>{index + 1}</Var>
                </T>
              </p>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                disabled={disabled}
                aria-label={gt('Remove action', {
                  context: 'icon button removing a custom task action',
                })}
                onClick={() =>
                  onChange(
                    drafts.filter(
                      (candidate) => candidate.clientId !== draft.clientId,
                    ),
                  )
                }
              >
                <TrashIcon className="size-3.5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label
                  htmlFor={`${draft.clientId}-key`}
                  className="text-[11px]"
                >
                  <T context="task action form field label">Key</T>
                </Label>
                <Input
                  id={`${draft.clientId}-key`}
                  className="h-8 font-mono text-xs"
                  value={draft.key}
                  disabled={disabled}
                  onChange={(e) =>
                    updateDraft(draft.clientId, { key: e.target.value })
                  }
                  placeholder={gt('flag-issue', {
                    context: 'placeholder for a task action key',
                  })}
                />
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor={`${draft.clientId}-label`}
                  className="text-[11px]"
                >
                  <T context="task action form field label">Button label</T>
                </Label>
                <Input
                  id={`${draft.clientId}-label`}
                  className="h-8 text-xs"
                  value={draft.label}
                  disabled={disabled}
                  onChange={(e) =>
                    updateDraft(draft.clientId, { label: e.target.value })
                  }
                  placeholder={gt('Flag issue', {
                    context: 'placeholder for a task action button label',
                  })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label
                  htmlFor={`${draft.clientId}-effect`}
                  className="text-[11px]"
                >
                  <T context="task action form field label">When clicked</T>
                </Label>
                <Select
                  value={draft.effectKind}
                  disabled={disabled}
                  onValueChange={(value) => {
                    if (
                      value === 'emit_event' ||
                      value === 'start_workflow' ||
                      value === 'set_outcome'
                    ) {
                      updateDraft(draft.clientId, {
                        effectKind: value,
                        effectTarget: '',
                      });
                    }
                  }}
                >
                  <SelectTrigger
                    id={`${draft.clientId}-effect`}
                    className="h-8"
                    aria-label={gt('When clicked for action {n}', {
                      context:
                        'accessible label for a custom task action effect picker',
                      n: String(index + 1),
                    })}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emit_event">
                      {gt('Emit a system event', {
                        context: 'task action effect kind option',
                      })}
                    </SelectItem>
                    <SelectItem value="start_workflow">
                      {gt('Start a workflow', {
                        context: 'task action effect kind option',
                      })}
                    </SelectItem>
                    <SelectItem value="set_outcome">
                      {gt('Record an outcome', {
                        context: 'task action effect kind option',
                      })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {draft.effectKind === 'set_outcome' ? (
                <ActionOutcomeStatusField
                  draft={draft}
                  disabled={disabled}
                  onChange={(outcomeStatus) =>
                    updateDraft(draft.clientId, { outcomeStatus })
                  }
                />
              ) : (
                <ActionEffectTargetField
                  draft={draft}
                  disabled={disabled}
                  onChange={(effectTarget) =>
                    updateDraft(draft.clientId, { effectTarget })
                  }
                />
              )}
            </div>
            {draft.effectKind === 'set_outcome' ? (
              <ActionOutcomeFields
                draft={draft}
                outcomeChoices={outcomeChoices}
                disabled={disabled}
                onChange={(patch) => updateDraft(draft.clientId, patch)}
              />
            ) : null}
            <ActionFormFieldsEditor
              actionClientId={draft.clientId}
              fields={draft.formFields}
              disabled={disabled}
              onChange={(formFields) =>
                updateDraft(draft.clientId, { formFields })
              }
            />
          </div>
        ))}

        <TaskTypeActionCloseGuardrails
          drafts={drafts}
          outcomeChoices={outcomeChoices}
          disabled={disabled}
          onAdd={(draft) => onChange([...drafts, draft])}
        />

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          disabled={disabled}
          onClick={() => onChange([...drafts, newActionDraft()])}
        >
          <PlusIcon className="size-3.5" />
          <T context="button, add a custom task action">Add action</T>
        </Button>

        {!parsed.ok && (
          <p className="text-[11px] text-destructive">
            {formatActionDraftError(gt, parsed.error)}
          </p>
        )}
      </div>
    </TaskTypeSection>
  );
}

function ActionEffectTargetField({
  draft,
  disabled,
  onChange,
}: {
  draft: TaskTypeActionDraft;
  disabled?: boolean;
  onChange: (effectTarget: string) => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1">
      <Label htmlFor={`${draft.clientId}-target`} className="text-[11px]">
        {draft.effectKind === 'emit_event' ? (
          <T context="task action form field label">Event key</T>
        ) : (
          <T context="task action form field label">Workflow template key</T>
        )}
      </Label>
      <Input
        id={`${draft.clientId}-target`}
        className="h-8 font-mono text-xs"
        value={draft.effectTarget}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          draft.effectKind === 'emit_event'
            ? gt('TASK_COMPLETED', {
                context: 'placeholder for a system event key',
              })
            : gt('escalation-review', {
                context: 'placeholder for a workflow template key',
              })
        }
      />
    </div>
  );
}

function ActionOutcomeStatusField({
  draft,
  disabled,
  onChange,
}: {
  draft: TaskTypeActionDraft;
  disabled?: boolean;
  onChange: (status: TaskTypeActionDraft['outcomeStatus']) => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1">
      <Label htmlFor={`${draft.clientId}-status`} className="text-[11px]">
        <T context="task action form field label">Close as</T>
      </Label>
      <Select
        value={draft.outcomeStatus}
        disabled={disabled}
        onValueChange={(value) => {
          if (
            value === 'completed' ||
            value === 'failed' ||
            value === 'cancelled'
          ) {
            onChange(value);
          }
        }}
      >
        <SelectTrigger id={`${draft.clientId}-status`} className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="completed">
            {gt('Completed', {
              context: 'task action outcome status option',
            })}
          </SelectItem>
          <SelectItem value="failed">
            {gt('Failed', { context: 'task action outcome status option' })}
          </SelectItem>
          <SelectItem value="cancelled">
            {gt('Cancelled', {
              context: 'task action outcome status option',
            })}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function ActionOutcomeFields({
  draft,
  outcomeChoices,
  disabled,
  onChange,
}: {
  draft: TaskTypeActionDraft;
  outcomeChoices: readonly string[];
  disabled?: boolean;
  onChange: (patch: Partial<TaskTypeActionDraft>) => void;
}) {
  const gt = useGT();
  const cancelling = draft.outcomeStatus === 'cancelled';
  const choices =
    draft.outcome && !outcomeChoices.includes(draft.outcome)
      ? [draft.outcome, ...outcomeChoices]
      : outcomeChoices;

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cancelling ? 'grid grid-cols-1' : 'grid grid-cols-2 gap-2'}
      >
        {cancelling ? null : (
          <div className="space-y-1">
            <Label
              htmlFor={`${draft.clientId}-outcome`}
              className="text-[11px]"
            >
              <T context="task action form field label">Outcome</T>
            </Label>
            {choices.length > 0 ? (
              <Select
                value={draft.outcome || undefined}
                disabled={disabled}
                onValueChange={(outcome) => onChange({ outcome })}
              >
                <SelectTrigger id={`${draft.clientId}-outcome`} className="h-8">
                  <SelectValue
                    placeholder={gt('Choose an outcome', {
                      context: 'placeholder for a task action outcome picker',
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {choices.map((choice) => (
                    <SelectItem key={choice} value={choice}>
                      {choice}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={`${draft.clientId}-outcome`}
                className="h-8 font-mono text-xs"
                value={draft.outcome}
                disabled={disabled}
                onChange={(e) => onChange({ outcome: e.target.value })}
                placeholder={gt('approved', {
                  context: 'placeholder for a task action outcome key',
                })}
              />
            )}
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor={`${draft.clientId}-reason`} className="text-[11px]">
            <T context="task action form field label">Outcome reason</T>
          </Label>
          <Input
            id={`${draft.clientId}-reason`}
            className="h-8 text-xs"
            value={draft.outcomeReason}
            disabled={disabled}
            onChange={(e) => onChange({ outcomeReason: e.target.value })}
            placeholder={
              cancelling
                ? gt('Required unless the form asks for it', {
                    context: 'placeholder for a cancellation outcome reason',
                  })
                : gt('Optional explanation', {
                    context: 'placeholder for a task action outcome reason',
                  })
            }
          />
        </div>
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        <T context="hint under a task action that records an outcome">
          Closes the task with this outcome. Workflows waiting on the task see
          the same result a complete-task node would write. A form field named
          outcomeReason overrides the reason. Other form fields whose keys match
          this type's outcome fields are saved as supporting data.
        </T>
      </p>
    </div>
  );
}

function ActionFormFieldsEditor({
  actionClientId,
  fields,
  disabled,
  onChange,
}: {
  actionClientId: string;
  fields: ActionFormFieldDraft[];
  disabled?: boolean;
  onChange: (next: ActionFormFieldDraft[]) => void;
}) {
  const gt = useGT();

  function updateField(clientId: string, patch: Partial<ActionFormFieldDraft>) {
    onChange(
      fields.map((field) =>
        field.clientId === clientId ? { ...field, ...patch } : field,
      ),
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border bg-stone-50/60 p-2">
      <div className="space-y-0.5">
        <p className="text-[11px] font-medium text-stone-700">
          <T context="heading for optional form fields on a task action">
            Form fields
          </T>
        </p>
        <p className="text-[11px] leading-snug text-muted-foreground">
          <T context="hint under task action form fields editor">
            Optional inputs shown in a dialog before the action runs. Leave
            empty for a one-click action (also available as a queue quick
            action).
          </T>
        </p>
      </div>

      {fields.map((field, index) => (
        <div
          key={field.clientId}
          className="space-y-2 rounded-md border border-border bg-white p-2"
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium text-stone-600">
              <T context="heading for one task action form field row">
                Field <Var>{index + 1}</Var>
              </T>
            </p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={disabled}
              aria-label={gt('Remove form field', {
                context: 'icon button removing a task action form field',
              })}
              onClick={() =>
                onChange(
                  fields.filter(
                    (candidate) => candidate.clientId !== field.clientId,
                  ),
                )
              }
            >
              <TrashIcon className="size-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label
                htmlFor={`${actionClientId}-${field.clientId}-key`}
                className="text-[11px]"
              >
                <T context="task action form field label">Field key</T>
              </Label>
              <Input
                id={`${actionClientId}-${field.clientId}-key`}
                className="h-8 font-mono text-xs"
                value={field.key}
                disabled={disabled}
                onChange={(e) =>
                  updateField(field.clientId, { key: e.target.value })
                }
                placeholder={gt('reason', {
                  context: 'placeholder for a task action form field key',
                })}
              />
            </div>
            <div className="space-y-1">
              <Label
                htmlFor={`${actionClientId}-${field.clientId}-label`}
                className="text-[11px]"
              >
                <T context="task action form field label">Field label</T>
              </Label>
              <Input
                id={`${actionClientId}-${field.clientId}-label`}
                className="h-8 text-xs"
                value={field.label}
                disabled={disabled}
                onChange={(e) =>
                  updateField(field.clientId, { label: e.target.value })
                }
                placeholder={gt('Reason', {
                  context: 'placeholder for a task action form field label',
                })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label
                htmlFor={`${actionClientId}-${field.clientId}-type`}
                className="text-[11px]"
              >
                <T context="task action form field label">Type</T>
              </Label>
              <Select
                value={field.type}
                disabled={disabled}
                onValueChange={(value) => {
                  const nextType = ACTION_FORM_FIELD_TYPE_OPTIONS.find(
                    (type) => type === value,
                  );
                  if (nextType) {
                    updateField(field.clientId, { type: nextType });
                  }
                }}
              >
                <SelectTrigger
                  id={`${actionClientId}-${field.clientId}-type`}
                  className="h-8"
                  aria-label={gt('Type for form field {n}', {
                    context:
                      'accessible label for a task action form field type picker',
                    n: String(index + 1),
                  })}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_FORM_FIELD_TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-1.5 text-[11px] text-stone-700">
                <input
                  type="checkbox"
                  className="size-3.5"
                  checked={field.required}
                  disabled={disabled}
                  onChange={(e) =>
                    updateField(field.clientId, {
                      required: e.target.checked,
                    })
                  }
                />
                <T context="checkbox label, task action form field is required">
                  Required
                </T>
              </label>
            </div>
          </div>
          <div className="space-y-1">
            <Label
              htmlFor={`${actionClientId}-${field.clientId}-help`}
              className="text-[11px]"
            >
              <T context="task action form field label">Help text</T>
            </Label>
            <Input
              id={`${actionClientId}-${field.clientId}-help`}
              className="h-8 text-xs"
              value={field.helpText}
              disabled={disabled}
              onChange={(e) =>
                updateField(field.clientId, { helpText: e.target.value })
              }
              placeholder={gt('Optional hint shown under the field', {
                context: 'placeholder for task action form field help text',
              })}
            />
          </div>
          {field.type === 'select' && (
            <div className="space-y-1">
              <Label
                htmlFor={`${actionClientId}-${field.clientId}-options`}
                className="text-[11px]"
              >
                <T context="task action form field label">
                  Options (comma-separated)
                </T>
              </Label>
              <Input
                id={`${actionClientId}-${field.clientId}-options`}
                className="h-8 text-xs"
                value={field.optionsText}
                disabled={disabled}
                onChange={(e) =>
                  updateField(field.clientId, {
                    optionsText: e.target.value,
                  })
                }
                placeholder={gt('approve, reject, needs-review', {
                  context:
                    'placeholder for comma-separated select options on a task action form field',
                })}
              />
            </div>
          )}
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        disabled={disabled}
        onClick={() => onChange([...fields, newActionFormFieldDraft()])}
      >
        <PlusIcon className="size-3.5" />
        <T context="button, add a form field to a task action">Add field</T>
      </Button>
    </div>
  );
}
